import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import Sidebar from './Sidebar/Sidebar';
import ScrollToTop from '../ui/ScrollToTop/ScrollToTop';
import LoadingSpinner from '../ui/LoadingSpinner/LoadingSpinner';
import Toast from '../ui/Toast/Toast';
import './Layout.css';

// Lazy load non-critical components
const CookieConsent = lazy(() => import('../ui/CookieConsent/CookieConsent'));
const ChatWidget = lazy(() => import('../ui/ChatWidget/ChatWidget'));

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const location = useLocation();

  // Track page views
  useEffect(() => {
    // Analytics tracking
    if (window.gtag) {
      window.gtag('config', 'GA_MEASUREMENT_ID', {
        page_path: location.pathname
      });
    }
  }, [location]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      showToast('You are back online!', 'success');
    };

    const handleOffline = () => {
      setIsOnline(false);
      showToast('You are offline. Some features may be unavailable.', 'warning');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check for saved theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Show welcome toast
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisited');
    if (!hasVisited) {
      setTimeout(() => {
        showToast('Welcome to my portfolio! 👋', 'info');
        localStorage.setItem('hasVisited', 'true');
      }, 1500);
    }
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'info' });
    }, 5000);
  };

  const handleThemeToggle = () => {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
    showToast(`Switched to ${newTheme} mode`, 'success');
  };

  return (
    <div className={`layout ${!isOnline ? 'offline' : ''}`}>
      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="skip-to-content">
        Skip to content
      </a>

      {/* Header */}
      <Header 
        toggleSidebar={toggleSidebar} 
        onThemeToggle={handleThemeToggle}
        isOnline={isOnline}
      />

      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />

      {/* Main Content */}
      <main id="main-content" className="main-content">
        <Suspense fallback={<LoadingSpinner fullScreen />}>
          <Outlet context={{ showToast }} />
        </Suspense>
      </main>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      {showScrollTop && <ScrollToTop />}

      {/* Toast Notifications */}
      <Toast 
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* Offline Indicator */}
      {!isOnline && (
        <div className="offline-indicator">
          <i className="fas fa-wifi-slash"></i>
          <span>You are offline</span>
        </div>
      )}

      {/* Lazy Loaded Components */}
      <Suspense fallback={null}>
        <CookieConsent />
        <ChatWidget />
      </Suspense>

      {/* Loading Bar for route transitions */}
      <div className="loading-bar" id="loading-bar"></div>
    </div>
  );
};

export default Layout;